import { definePreset, palette } from '@primeng/themes';
import { CapturumUIPreset } from '@capturum/ui/config';

const appPalette = palette('#009ee0');
const projectColors = {
  primary: '#145996', // From $primary
  secondary: '#c91f55', // From $secondary
  primaryLight: '#009ee0', // From app.config primaryColor

  // Neutrals
  white: '#ffffff',
  black: '#000000',

  // Grays
  lightGray: '#f0f0f0', // From $very-light-gray
  mediumGray: '#ddd', // From $gray
  darkGray: '#5a5a5a', // From $dark-gray
  midGray: '#5f6062', // From $mid-gray
  tuatara: '#3c3c3b', // From $tuatara
  albaster: '#f9f9f9', // From $albaster
  secondaryBg: '#f4f5f6', // From $secondary-bg

  // Semantic colors
  success: '#82ad11', // From $very-light-green
  danger: '#e02020', // From $red
  warning: '#f5a623', // From $gold
  info: '#155995', // From $blue

  // Borders
  borderGray: '#ddd', // From $gray
  borderRadius: '3px', // From $default-border-radius
};

const commonFormField = {
  background: projectColors.white,
  disabledBackground: projectColors.albaster,
  filledBackground: projectColors.secondaryBg,
  filledFocusBackground: projectColors.white,
  borderColor: projectColors.borderGray,
  hoverBorderColor: projectColors.primaryLight,
  focusBorderColor: projectColors.primaryLight,
  invalidBorderColor: projectColors.danger,
  color: projectColors.darkGray,
  disabledColor: projectColors.midGray,
  placeholderColor: projectColors.midGray,
  iconColor: projectColors.midGray,
  shadow: 'none',
};

const commonFocusRing = {
  width: '0',
  style: 'none',
  color: 'unset',
  offset: '0',
  shadow: 'none',
};

export const appPreset = definePreset(CapturumUIPreset, {
  primitive: {
    capturum: appPalette,
  },
  semantic: {
    primary: {
      50: '{capturum.50}',
      100: '{capturum.100}',
      200: '{capturum.200}',
      300: '{capturum.300}',
      400: '{capturum.400}',
      500: '{capturum.500}',
      600: '{capturum.600}',
      700: '{capturum.700}',
      800: '{capturum.800}',
      900: '{capturum.900}',
      950: '{capturum.950}',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: projectColors.white,
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },
        text: {
          color: projectColors.darkGray,
          hoverColor: projectColors.tuatara,
          mutedColor: projectColors.midGray,
        },
        content: {
          background: projectColors.white,
          borderColor: projectColors.borderGray,
          color: projectColors.darkGray,
        },
        overlay: {
          modal: {
            background: projectColors.white,
            borderColor: projectColors.borderGray,
            color: projectColors.darkGray,
            shadow: '0px 5px 13px 0px rgba(95, 96, 98, 0.15)',
            borderRadius: '0',
          },
        },
        formField: {
          ...commonFormField,
          floatLabelColor: projectColors.midGray,
          floatLabelFocusColor: projectColors.darkGray,
          floatLabelActiveColor: projectColors.darkGray,
        },
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: projectColors.borderRadius,
      },
    },
    select: {
      root: {
        borderRadius: projectColors.borderRadius,
        background: '{formField.background}',
        disabledBackground: '{formField.disabledBackground}',
        borderColor: '{formField.borderColor}',
        hoverBorderColor: '{formField.hoverBorderColor}',
        focusBorderColor: '{formField.focusBorderColor}',
        invalidBorderColor: '{formField.invalidBorderColor}',
        color: '{formField.color}',
        disabledColor: '{formField.disabledColor}',
        placeholderColor: '{formField.placeholderColor}',
        shadow: '{formField.shadow}',
        paddingX: '0.75rem',
        paddingY: '0.5rem',
        focusRing: commonFocusRing,
      },
      dropdown: {
        width: '2rem',
      },
      list: {
        padding: '0',
        header: {
          padding: '0',
        },
      },
      option: {
        padding: '0.5rem 0.75rem',
        borderRadius: '0',
        selectedBackground: projectColors.lightGray,
        selectedColor: projectColors.tuatara,
        selectedFocusBackground: projectColors.lightGray,
        selectedFocusColor: projectColors.tuatara,
      },
    },
    inputtext: {
      root: {
        background: '{formField.background}',
        disabledBackground: '{formField.disabledBackground}',
        filledBackground: '{formField.filledBackground}',
        filledFocusBackground: '{formField.filledFocusBackground}',
        borderColor: '{formField.borderColor}',
        hoverBorderColor: '{formField.hoverBorderColor}',
        focusBorderColor: '{formField.focusBorderColor}',
        invalidBorderColor: '{formField.invalidBorderColor}',
        color: '{formField.color}',
        disabledColor: '{formField.disabledColor}',
        placeholderColor: '{formField.placeholderColor}',
        shadow: '{formField.shadow}',
        paddingX: '0.75rem',
        paddingY: '0.5rem',
        borderRadius: projectColors.borderRadius,
        focusRing: commonFocusRing,
      },
    },
    dialog: {
      root: {
        borderRadius: '0',
      },
      title: {
        fontSize: '1.3rem',
        fontWeight: 'bold',
      },
      content: {
        padding: '1.5rem',
      },
      footer: {
        padding: '1.25rem',
      },
    },
    datepicker: {
      root: {
        borderRadius: projectColors.borderRadius,
      },
      panel: {
        borderRadius: '6px',
        shadow: '0px 5px 13px 0px rgba(95, 96, 98, 0.15)',
      },
    },
    toast: {
      root: {
        borderRadius: projectColors.borderRadius,
      },
      colorScheme: {
        light: {
          success: {
            background: projectColors.success,
            borderColor: projectColors.success,
            color: projectColors.white,
            closeButton: {
              hoverBackground: 'transparent',
            },
          },
          error: {
            background: projectColors.danger,
            borderColor: projectColors.danger,
            color: projectColors.white,
          },
          warn: {
            background: projectColors.warning,
            borderColor: projectColors.warning,
            color: projectColors.white,
          },
          info: {
            background: projectColors.info,
            borderColor: projectColors.info,
            color: projectColors.white,
          },
        },
      },
    },
    accordion: {
      header: {
        fontWeight: 'bold',
        borderColor: 'transparent',
        color: projectColors.darkGray,
        background: projectColors.lightGray,
        hoverBackground: projectColors.lightGray,
        activeBackground: projectColors.lightGray,
        activeHoverBackground: projectColors.lightGray,
        activeColor: projectColors.darkGray,
      },
      content: {
        borderColor: 'transparent',
        padding: '1rem',
      },
    },
    datatable: {
      headerCell: {
        background: projectColors.white,
        hoverBackground: '#e0e0e0',
        borderColor: 'transparent',
        color: projectColors.darkGray,
        hoverColor: projectColors.darkGray,
        gap: '0.5rem',
      },
      bodyCell: {
        borderColor: 'transparent',
      },
      row: {
        background: projectColors.white,
      },
      borderColor: 'transparent',
    },
    popover: {
      root: {
        borderRadius: '6px',
      },
      content: {
        padding: '0',
      },
    },
    radiobutton: {
      checked: {
        background: projectColors.white,
        hover: {
          background: projectColors.white,
        },
      },
      root: {
        width: '1.375rem',
        height: '1.375rem',
        checkedBorderColor: projectColors.primary,
        checkedHoverBorderColor: projectColors.primary,
        checkedFocusBorderColor: projectColors.primary,
      },
      icon: {
        checkedColor: projectColors.primary,
        checkedHoverColor: projectColors.primary,
      },
    },
    selectbutton: {
      border: {
        radius: '18px',
      },
    },
    togglebutton: {
      colorScheme: {
        light: {
          root: {
            background: projectColors.white,
            checkedBackground: projectColors.primary,
            hoverBackground: projectColors.lightGray,
            borderColor: projectColors.primary,
            color: projectColors.primary,
            hoverColor: projectColors.primary,
            checkedColor: projectColors.white,
            checkedBorderColor: projectColors.primary,
          },
        },
      },
    },
    checkbox: {
      checked: {
        background: projectColors.primary,
        borderColor: projectColors.primary,
        hover: {
          background: projectColors.primary,
          borderColor: projectColors.primary,
        },
      },
      icon: {
        checked: {
          color: projectColors.white,
        },
      },
    },
    paginator: {
      navButton: {
        color: projectColors.primary,
        selectedBackground: projectColors.primary,
      },
    },
    toggleswitch: {
      focus: {
        ring: {
          shadow: 'none',
          color: 'transparent',
        },
      },
      handle: {
        disabled: {
          background: projectColors.white,
        },
      },
      disabled: {
        background: '#cbd5e1',
      },
    },
  },
});
